import { observer } from "mobx-react";
import * as React from "react";
import { PageUserSettingsTab, routePath } from "../route-path";
import { ListUsers } from "../ui/molecules/list-users";
import { ListCommandButtonsUsers } from "../ui/organisms/list-command-buttons-user";
import { createUuid } from "../utils/uuid";
import { Section } from "../ui/atoms/section";
import { Pivot, PivotItem, Stack, type IPivotItemProps } from "@fluentui/react";
import { ListUserGroups } from "../ui/molecules/list-user-groups";
import { ListCommandButtonsUserGroups } from "../ui/organisms/list-command-buttons-user-groups";
import sizes from "../ui/sizes.scss";
import { ListUserGroupAddButton } from "../ui/organisms/list-user-group-add-button";
import { type Location } from "history";
import { action, makeObservable, observable } from "mobx";
import { articles } from "../help";
import { store } from "../store";
import { FusionLayout } from "@/parts/fusion-layout";
import { Users } from "lucide-react";

const customPivotItemRenderer = (
    link?: IPivotItemProps,
    defaultRenderer?: (link?: IPivotItemProps) => JSX.Element | null,
): JSX.Element | null => {
    if (!link || !defaultRenderer) {
        return null;
    }

    return (
        <span style={{ display: "flex", alignItems: "center" }}>
            <Users style={{ width: 20, paddingRight: 5, display: "inline-flex" }} />
            <div>{defaultRenderer({ ...link })}</div>
        </span>
    );
};

@observer
export class PageUserSettings extends React.Component {
    // Create the UUID on this level, so the button bar is able to access the same state as the list.
    private userListStateId = createUuid();
    private userGroupListStateId = createUuid();

    // This is updated by manually listening to changes in the browser history.
    // Since the history is not reactive itself, we cannot move this into a @computed getter.
    @observable private accessor selectedTab: string = PageUserSettingsTab.Users;

    constructor(props: {}) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.historyProvider.history.listen(({ location }) => this.updateSelectedTab(location));
        this.updateSelectedTab(store.historyProvider.history.location);
    }

    // This gets called when the user clicks a different tab.
    // It pushes a new entry into the browser history.
    // The change in the browser history will result in a call to `updateSelectedTab`
    // afterwards.
    @action.bound
    private updateURL(item?: PivotItem): void {
        if (item?.props.itemKey) {
            store.historyProvider.history.push(
                routePath.userSettings(item.props.itemKey as PageUserSettingsTab),
            );
        }
    }

    // This gets called when the history is updated in any way.
    // It recalculates the `selectedTab` value.
    @action.bound
    private updateSelectedTab(location: Location): void {
        // NOTE: when rewriting as a functional component use useLocation hook instead
        const tab = location.pathname.split("/").slice(-1)[0];

        if (typeof tab !== "string") {
            return;
        }

        this.selectedTab = tab;
    }

    public render(): JSX.Element {
        return (
            <FusionLayout
                suggestedHelpArticles={[
                    articles.adminManageUsers,
                    articles.howToAddUser,
                    articles.userDefinition,
                    articles.fleetUserRoles,
                    articles.managementUserRoles,
                    articles.howToAccessInternalNfcId,
                    articles.howToCreateAccessControlGroups,
                    articles.administratorFunctionalities,
                    articles.downloadDocuments,
                ]}
                breadcrumb={store.i18n.t("page.userSettings.title")}
                classic
            >
                <Pivot
                    linkSize="large"
                    styles={{
                        root: {
                            display: "flex",
                            justifyContent: "center",
                            height: sizes.pivotHeight,
                        },
                        link: { marginBottom: `${sizes.gutter}` },
                    }}
                    onLinkClick={this.updateURL}
                    selectedKey={this.selectedTab}
                >
                    <PivotItem
                        headerText={store.i18n.t("page.userSettings.tab.user")}
                        itemKey={PageUserSettingsTab.Users}
                        onRenderItemLink={customPivotItemRenderer}
                    >
                        <Section>
                            <ListUsers
                                commandBar={
                                    <Stack horizontal style={{ alignItems: "center" }}>
                                        <ListCommandButtonsUsers
                                            listStateId={this.userListStateId}
                                            canDelete
                                        />
                                    </Stack>
                                }
                                listStateId={this.userListStateId}
                                ignoreColumnVisibility={true}
                                maxHeight={`${sizes.listMaxHeightWithPivots}`}
                            />
                        </Section>
                    </PivotItem>
                    <PivotItem
                        headerText={store.i18n.t("page.userSettings.tab.userGroup")}
                        itemKey={PageUserSettingsTab.UserGroups}
                        onRenderItemLink={customPivotItemRenderer}
                    >
                        <Section>
                            <ListUserGroups
                                commandBar={
                                    <Stack horizontal>
                                        <ListUserGroupAddButton
                                            listStateId={this.userGroupListStateId}
                                        />
                                        <ListCommandButtonsUserGroups
                                            listStateId={this.userGroupListStateId}
                                            canDelete
                                        />
                                    </Stack>
                                }
                                listStateId={this.userGroupListStateId}
                                showSearchField
                                maxHeight={`${sizes.listMaxHeightWithPivots}`}
                            />
                        </Section>
                    </PivotItem>
                </Pivot>
            </FusionLayout>
        );
    }
}
